import {
  ArrowPathRoundedSquareIcon,
  ArrowsRightLeftIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  LightBulbIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

import type { AcademicInsight } from '@/app/types/academicInsights';
import { rawColorUsage } from '@/app/design-system/colorUsage';

const iconByVisual = {
  stat: ChartBarSquareIcon,
  meter: ChartBarSquareIcon,
  'stacked-bar': ChartBarSquareIcon,
  comparison: ArrowsRightLeftIcon,
  'topic-cluster': ShareIcon,
  'learning-loop': ArrowPathRoundedSquareIcon,
  pathway: ShareIcon,
  'relationship-node': ShareIcon,
  timeline: CalendarDaysIcon,
};

const accentByVisual = {
  stat: '#cc0033', meter: '#cc0033', 'stacked-bar': '#0f766e', comparison: '#7c3aed',
  'topic-cluster': '#2563eb', 'learning-loop': '#0f766e', pathway: '#2563eb',
  'relationship-node': '#d97706', timeline: '#7c3aed',
};

export function AcademicInsightGallery({ insights }: { insights: AcademicInsight[] }) {
  if (!insights.length) return null;
  return (
    <section aria-labelledby="academic-insights-heading">
      <div className="mb-3 flex items-center gap-2">
        <LightBulbIcon className="h-5 w-5 text-[var(--ss-accent)]" aria-hidden="true" />
        <h2 id="academic-insights-heading" className="text-base font-semibold text-[var(--ss-text)]">Worth knowing</h2>
        <span className="rounded-full bg-[var(--ss-accent-subtle)] px-2 py-0.5 text-xs font-semibold text-[var(--ss-accent)]">AI-generated</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {insights.slice(0, 6).map((insight) => {
          const Icon = iconByVisual[insight.visual];
          const color = rawColorUsage({ purpose: 'visualization', slot: 'accent', value: accentByVisual[insight.visual] });
          return (
            <article key={insight.id} {...color} className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 shadow-e1">
              <div className="flex items-start justify-between gap-3">
                <span className="rounded-control bg-[color-mix(in_srgb,var(--ss-raw-color-accent)_12%,transparent)] p-2 text-[var(--ss-raw-color-accent)]"><Icon className="h-5 w-5" aria-hidden="true" /></span>
                {insight.value ? <span className="text-right text-sm font-bold text-[var(--ss-raw-color-accent)]">{insight.value}</span> : null}
              </div>
              <h3 className="mt-3 font-semibold text-[var(--ss-text)]">{insight.title}</h3>
              <p className="mt-1 text-sm leading-6 text-[var(--ss-text-muted)]">{insight.summary}</p>
              {insight.relatedEntityIds.length ? <p className="mt-3 truncate text-xs font-medium text-[var(--ss-text-muted)]">{insight.relatedEntityIds.length} connected {insight.relatedEntityIds.length === 1 ? 'entity' : 'entities'}</p> : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}
